/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import handler from '../../../../share/util/apiResultValidator';
import api from '@/common/api';

/**
 * 各フェーズ定義
 */
const PHASE = {
  INITIAL_WAITING: 'initialWaiting',
  EXECUTION_CONFIRMATION: 'executionConfirmation',
  EXECUTING: 'executing',
  SUCCESS: 'success',
  EXECUTION_FAILED: 'executionFailed'
};

export default {
  name: 'requirement137',
  data() {
    return {
      phase: PHASE.INITIAL_WAITING,
      isLoading: true,
      items: [],
      systemId: '',
      maxNum: 2,
      minNum: 1,
      isChecked: [],
      isDisabled: [],
      isButtonDisabled: true,
      responseUserData: [],
      columns: [
        { name: 'number', label: `${this.i18nWsKey}.table_column_number` },
        { name: 'class', label: `${this.i18nWsKey}.table_column_class` },
        { name: 'dtc', label: `${this.i18nWsKey}.table_column_dtc` },
        { name: 'started_at', label: `${this.i18nWsKey}.table_column_started` },
        { name: 'finished_at', label: `${this.i18nWsKey}.table_column_finished` },
        { name: 'check', label: `${this.i18nWsKey}.table_column_check`, useCheckbox: true },
      ],
      modal_requirement_137: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.EXECUTION_FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.INITIAL_WAITING:
        case PHASE.EXECUTION_CONFIRMATION:
        case PHASE.EXECUTING:
        default:
          return 'secondary';
      }
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_137 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // 待機処理
      this.execute();
    },

    /** 待機処理 */
    async execute() {
      try {
        this.isLoading = true;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // 正常コールバック
        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：一覧データ取得処理
              this.phase = PHASE.EXECUTION_CONFIRMATION;
              // データ表示用変換
              this.$nextTick(() => {
                for (const element of response.data.user_data.result) {
                  if (element.dtc === null) element.dtc = "-";
                  if (element.started_at === null) element.started_at = "-";
                  if (element.finished_at === null) element.finished_at = "-";
                  this.items = this.items.concat(element);
                  this.isChecked.push(false);
                  this.isDisabled.push(false);
                }
              });
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 異常終了：エラー画面に遷移
              this.showUnexpectedError();
              return;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** アップロード処理 */
    async upload() {
      try {
        this.isLoading = true;
        this.phase = PHASE.EXECUTING;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'upload',
            did_list: this.generateDidList()
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // リトライ回数を指定：10800回（1秒間隔で180分）
          process.env.VUE_APP_WORK_SUPPORT_RETRY_COUNT_LONG,
        );

        // 正常コールバック
        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              this.phase = PHASE.SUCCESS;
              // 取得したユーザーデータの結果
              this.responseUserData = response.data.user_data;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 異常終了：エラー画面に遷移
              this.showUnexpectedError();
              return;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** ダウンロード処理 */
    async downloadFile() {
      try {
        this.isLoading = true;

        for (let index = 0; index < this.responseUserData.length; index++) {
          const filePathInfo = this.responseUserData[index].file_path;
          // ダウンロードURL処理
          const params = {
            file_path: filePathInfo
          };
          const downloadFileName = this.getDownloadFileName(this.responseUserData[index].id);
          const response = await api.getParamCall(config.WSP, '/downloadUrl', params);
          // ダウンロードURL処理のResponseをチェックする
          handler.validate(
            handler.validateTypes.all,
            response, this, null,
            () => {// 処理が成功したときのコールバック処理
              try {
                //URLからファイルをダウンロード
                fetch(response.data.fileUrl)
                  .then((response) => response.blob())
                  .then((myBlob) => {
                    const objUrl = URL.createObjectURL(myBlob);
                    const link = document.createElement("a");
                    link.href = objUrl;
                    // ファイル名
                    link.download = downloadFileName;
                    link.click();
                    link.remove();
                    URL.revokeObjectURL(objUrl);
                  });
              } catch {
                this.phase = PHASE.EXECUTION_FAILED;
              }
            },
            (response) => {// 処理が失敗したときのコールバック処理
              if (response.causeType === handler.causeTypes.permissionError) {
                // ユーザー権限エラーの場合、エラーダイアログを表示しログアウトする
                alert.showError(this, null, response.message, () => {
                  this.logout(this);
                });
              } else {
                this.phase = PHASE.EXECUTION_FAILED;
              }
            }, null, false
          );
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async exit() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
      } finally {
        this.isLoading = false;
        // 結果にかかわらず機能終了
        this.closeWizard();
      }
    },

    // ダウンロードするログファイル名を引き当て
    getDownloadFileName(targetDid) {
      let downloadFileName = null;
      const targetLogData = this.items.find(item => item.did === targetDid);

      // 種別がDTCの場合
      if (targetLogData.class === 'DTC') {
        downloadFileName = `${targetLogData.dtc}.zip`;
      } else if (targetLogData.class === 'Performance') {
        // 種別がPerformanceの場合、started_at, finished_atをファイル名に整形する
        downloadFileName = `${targetLogData.started_at.replace(/[ :/]/g, '')}_${targetLogData.finished_at.replace(/[ :/]/g, '')}.zip`;
      }

      return downloadFileName;
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.EXECUTION_FAILED;
    },

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result handler.validate で返ってきたバリデーション結果
    */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_137 = false;
    },

    /**
    * チェックボックスクリック処理（個数制限あり）
    * @param int index テーブルの行インデックス
    */
    checkboxClicked(index) {
      // チェックボックス情報を更新
      if (this.isChecked[index]) {
        this.isChecked[index] = false;
      } else {
        this.isChecked[index] = true;
      }

      // チェックされている数の取得
      const checkCnt = this.isChecked.filter(element => {
        return element === true;
      });

      // 取得ボタンの制御
      if (checkCnt.length < this.minNum) {
        this.isButtonDisabled = true;
      } else {
        this.isButtonDisabled = false;
      }

      // リミットまでチェックされている場合は他のチェックボックスを非活性
      // それ以外の場合はすべて活性
      if (checkCnt.length === this.maxNum) {
        for (let i = 0; i < this.items.length; i++) {
          if (!this.isChecked[i]) {
            this.isDisabled[i] = true;
          }
        }
      } else {
        this.isDisabled.fill(false);
      }
    },

    /** リクエストパラメータ用配列生成 */
    generateDidList() {
      const didList = [];
      for (let i = 0; i < this.isChecked.length; i++) {
        if (this.isChecked[i]) {
          didList.push(this.items[i].did);
        }
      }
      return didList;
    },

    /**
    * テーブル列に適用するスタイルを指定
    * @param {Array} data 画面データ
    * @param String columnName カラム名
    */
    getColumnClasses(data, columnName) {
      if (!data.fieldSpec || !data.fieldSpec[columnName]) {
        return {};
      }

      const columnConfig = data.fieldSpec[columnName];
      return {
        'field-highlight': columnConfig === config.LIST_ITEM_HIGHLIGHT,
        'field-error': columnConfig === config.LIST_ITEM_ERROR,
        'field-bold': columnConfig === config.LIST_ITEM_BOLD || columnConfig === config.LIST_ITEM_HIGHLIGHT,
        'field-disable': columnConfig === config.LIST_ITEM_DISABLE
      };
    },

    /**
    * テーブル行に適用するスタイルを指定
    * @param String style メインフローから指定されるスタイル
    * @param int index カラム名
    */
    getTdStyle(style, index) {
      return { [style]: true, 'table-row-stripe': (index % 2 === 0) && true };
    },

  },

  mounted() {
    this.init();
  }
};